import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';

import { ExpiredOverlay, CartStatusEnum } from '@pegasus/cart/components/expired-overlay';
import { LanguageCode } from '@pegasus/translation/types';
import { useTranslationContext } from '@pegasus/translation/hooks';
import { Header, GuestHeader } from '@pegasus/ui-headers';
import {
    CookieConsent,
    setCookie,
    isAccepted,
} from '@pegasus/ui-headers/components/cookie-consent';

import { useConfig } from 'client/config';

import { Timer } from '../cart/components/timer';
import { extendCart, removeItem } from '../cart/service';
import { fetchCart, resetCart } from '../cart/redux';
import { useCartWithPrefetch } from '../cart/redux/hooks';

import { useOpenCampaignsWithPrefetch } from './hooks';
import { trackOrderFilter } from './tracking';

import { CartItem } from '@pegasus/ui-headers/components/Header/Cart/types';

type OwnProps = {
    isGuest?: boolean;
};

export const _HeaderWrapper = ({ location, isGuest = false }: OwnProps & RouteComponentProps) => {
    const config = useConfig();
    const { translations } = useTranslationContext();
    const dispatch = useDispatch();
    const cart = useCartWithPrefetch();
    const campaigns = useOpenCampaignsWithPrefetch();
    const [timerState, setTimerState] = useState<CartStatusEnum | undefined>(undefined);

    const renderTimer = useMemo(
        () => () => (
            <Timer
                cart={cart}
                onCartExpiresFirstWarning={() => {
                    setTimerState(CartStatusEnum.CART_EXPIRE_IN_5_MINUTES);
                }}
                onCartExpiresSecondWarning={() => {
                    setTimerState(CartStatusEnum.CART_EXPIRE_IN_SHORT_PERIOD);
                }}
                onCartExpiresLastWarning={() => {
                    setTimerState(CartStatusEnum.CART_EXPIRE_IN_SHORT_PERIOD);
                }}
                onCartExpired={() => {
                    setTimerState(CartStatusEnum.CART_EXPIRED);
                }}
            />
        ),
        [cart],
    );

    const languageSwitcherConfig = (config.languages || []).map(language => {
        return {
            ...language,
            url: `${language.url}${location.pathname}`,
        };
    });

    return (
        <>
            {isGuest ? (
                <GuestHeader
                    translations={translations}
                    languageCode={translations.locale as LanguageCode}
                    supportedLanguages={languageSwitcherConfig}
                    isPrive={config.isEnabled('isPrive')}
                />
            ) : (
                <Header
                    /* eslint-disable-next-line global-require */
                    translations={translations}
                    languageCode={translations.locale as LanguageCode}
                    csrfToken={typeof window !== 'undefined' ? window.CSRF : ''}
                    cart={
                        cart
                            ? {
                                  items: cart.items.map(item => ({
                                      ...item,
                                      imageUrl: `${config.getUrl('items')}zlcart2/${item.imageUrl}`,
                                  })),
                                  info: cart,
                              }
                            : undefined
                    }
                    campaigns={campaigns.map(campaign => ({
                        ...campaign,
                        imageUrl: campaign.imageUrl
                            ? `${config.getUrl('campaigns')}zlevent/${campaign.imageUrl}`
                            : undefined,
                    }))}
                    cartEvents={{
                        onItemRemove: simpleSku => {
                            const item =
                                cart && cart.items.find((i: CartItem) => i.simpleSku === simpleSku);
                            return removeItem({ simpleSku }).then(_ => {
                                if (item) trackOrderFilter(item);
                                dispatch(fetchCart());
                            });
                        },
                    }}
                    timer={renderTimer}
                    supportedLanguages={languageSwitcherConfig}
                    isPrive={config.isEnabled('isPrive')}
                />
            )}
            <CookieConsent
                translations={translations}
                accepted={isAccepted()}
                onClose={setCookie}
            />
            {
                <ExpiredOverlay
                    translations={translations}
                    isShown={!!timerState}
                    status={timerState || CartStatusEnum.CART_EXPIRED}
                    closeDialog={() => {
                        if (timerState === CartStatusEnum.CART_EXPIRED) {
                            dispatch(resetCart());
                        }

                        setTimerState(undefined);
                    }}
                    extendCart={() => {
                        extendCart().then(() => {
                            dispatch(fetchCart());
                            setTimerState(undefined);
                        });
                    }}
                />
            }
        </>
    );
};

export const HeaderWrapper: React.ComponentClass<OwnProps> = withRouter(_HeaderWrapper);
